import React, { useRef } from 'react';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import CheckIcon from '@heroicons/react/24/outline/CheckIcon';
import { Button } from '@/components/ui/atoms/Button';
import { createGTMTracking, getGTMAttributes } from '../../utils/gtm/gtm-tracking';

interface Feature {
    text: string;
}

interface ProcessStepV2Props {
    title: string;
    description: string;
    features: Feature[];
    imageSrc: string;
    imageAlt: string;
    imagePosition: 'left' | 'right';
    className?: string;
    step: 'step_1' | 'step_2' | 'step_3';
}

const ProcessStepV2: React.FC<ProcessStepV2Props> = ({
    title,
    description,
    features,
    imageSrc,
    imageAlt,
    imagePosition,
    className = "",
    step
}) => {
    const { t } = useTranslation('marketing');
    const navigate = useNavigate();
    const handleFreeTrialClick = () => {
        navigate('/signup?plan=free_trial');
    };
    const stepRef = useRef<HTMLDivElement>(null);

    const textContent = (
        <motion.article
            initial={{ opacity: 0, x: imagePosition === 'left' ? 30 : -30, scale: 0.95 }}
            whileInView={{ opacity: 1, x: 0, scale: 1 }}
            viewport={{ once: true, amount: 0.05 }}
            transition={{ duration: 0.5, delay: 0, ease: [0.25, 0.46, 0.45, 0.94] }}
            className="flex flex-col justify-center items-start gap-3 sm:gap-4 relative shadow-[0_2px_10px_0_rgba(0,0,0,0.10)] box-border bg-white px-5 sm:px-8 lg:px-16 py-5 sm:py-8 lg:py-16 rounded-[20px] w-full max-w-md lg:max-w-xl"
        >
            <header>
                <h3 className="text-[#1B1B28] text-xl sm:text-2xl lg:text-4xl font-bold leading-tight sm:leading-[32px] lg:leading-[48px] tracking-[-0.16px]"
                    dangerouslySetInnerHTML={{ __html: title }}
                />
            </header>

            <p className="text-dark-invirtus/70 text-sm sm:text-base font-normal leading-relaxed mb-2">
                {description}
            </p>

            <div className="flex items-center gap-3 sm:gap-4 lg:gap-5 mb-2 flex-wrap max-sm:flex-col max-sm:items-start max-sm:gap-2.5">
                {features.map((feature, index) => (
                    <div
                        key={index}
                        className="flex items-center gap-1 max-sm:w-full"
                    >
                        <CheckIcon className="w-4 h-4 sm:w-5 sm:h-5" />
                        <span className="text-dark-invirtus text-xs sm:text-sm font-bold leading-4 whitespace-nowrap">
                            {feature.text}
                        </span>
                    </div>
                ))}
            </div>

            <div className="flex items-start gap-3 sm:gap-4 px-0 py-1 sm:py-2">
                <Button
                    type="contained"
                    intent="primary"
                    size="md"
                    label={t('cta.free_trial')}
                    rightIcon={<ArrowRight />}
                    rightIconWithBackground
                    onClick={handleFreeTrialClick}
                    aria-label={t('cta.free_trial_aria')}
                    className={`w-full sm:w-auto ${className}`}
                    {...getGTMAttributes(createGTMTracking('NAVIGATE_SECTION', { origin: `process_${step}`, target: 'pricing_free_trial' }))}
                />
            </div>
        </motion.article>
    );

    const imageContent = (
        <motion.div
            initial={{ opacity: 0, x: imagePosition === 'left' ? -30 : 30, scale: 0.95 }}
            whileInView={{ opacity: 1, x: 0, scale: 1 }}
            viewport={{ once: true, amount: 0.05 }}
            transition={{ duration: 0.5, delay: 0.2, ease: [0.25, 0.46, 0.45, 0.94] }}
            className="p-6 sm:p-8 lg:p-12 rounded-2xl bg-light-lin  flex items-center justify-center w-full max-w-3xl"
        >
            <div className="flex items-center gap-1">
                <img
                    src={imageSrc}
                    alt={imageAlt}
                    className="w-full h-full object-cover" />
            </div>
        </motion.div>
    );


    return (
        <div ref={stepRef} className={`relative w-full flex flex-col items-center max-md:gap-4 ${className}`}>
            {/* Desktop Layout */}
            <div className="hidden md:flex w-full justify-center items-center relative ">
                <div className="relative flex items-center">
                    <div className="relative">
                        {imagePosition === 'left' ? imageContent : textContent}
                        <motion.div
                            initial={{ opacity: 0, scale: 0 }}
                            whileInView={{ opacity: 1, scale: 1 }}
                            viewport={{ once: true, amount: 0.05 }}
                            transition={{ duration: 0.4, delay: 0.3, ease: [0.25, 0.46, 0.45, 0.94] }}
                            className="absolute top-1/2 -right-2 w-3.5 h-3.5 bg-white border-[3.5px] border-[#A2A5A9] rounded-full transform -translate-y-1/2 z-10"
                        />
                    </div>

                    <div className="relative flex items-center justify-center w-28 h-6">
                        {imagePosition === 'left' ? (
                            <motion.div
                                initial={{ width: 0 }}
                                whileInView={{ width: "110px" }}
                                viewport={{ once: true, amount: 0.05 }}
                                transition={{ duration: 0.6, delay: 0.4, ease: [0.25, 0.46, 0.45, 0.94] }}
                                className="h-0.75 bg-[#A2A5A9] ml-auto"
                                style={{ transformOrigin: 'right center' }}
                            />
                        ) : (
                            <motion.div
                                initial={{ width: 0 }}
                                whileInView={{ width: "110px" }}
                                viewport={{ once: true, amount: 0.05 }}
                                transition={{ duration: 0.6, delay: 0.4, ease: [0.25, 0.46, 0.45, 0.94] }}
                                className="h-0.75 bg-[#A2A5A9] mr-auto"
                                style={{ transformOrigin: 'left center' }}
                            />
                        )}
                    </div>

                    <div className="relative">
                        {imagePosition === 'left' ? textContent : imageContent}
                        <motion.div
                            initial={{ opacity: 0, scale: 0 }}
                            whileInView={{ opacity: 1, scale: 1 }}
                            viewport={{ once: true, amount: 0.05 }}
                            transition={{ duration: 0.4, delay: 0.5, ease: [0.25, 0.46, 0.45, 0.94] }}
                            className="absolute top-1/2 -left-2 w-3.5 h-3.5 bg-white border-[3.5px] border-[#A2A5A9] rounded-full transform -translate-y-1/2 z-10"
                        />
                    </div>
                </div>
            </div>

            {/* Mobile Layout */}
            <div className="md:hidden w-full flex flex-col items-center justify-center">
                <div className="relative w-full flex items-center justify-center">
                    {textContent}
                    <motion.div
                        initial={{ opacity: 0, scale: 0 }}
                        whileInView={{ opacity: 1, scale: 1 }}
                        viewport={{ once: true, amount: 0.05 }}
                        transition={{ duration: 0.4, delay: 0.3, ease: [0.25, 0.46, 0.45, 0.94] }}
                        className="absolute -bottom-2 left-1/2 w-3.5 h-3.5 bg-white border-[3.5px] border-[#A2A5A9] rounded-full transform -translate-x-1/2 z-10"
                    />
                </div>

                <div className="relative flex flex-col items-center w-8 h-20">
                    <motion.div
                        initial={{ height: 0 }}
                        whileInView={{ height: "75px" }}
                        viewport={{ once: true, amount: 0.05 }}
                        transition={{ duration: 0.6, delay: 0.4, ease: [0.25, 0.46, 0.45, 0.94] }}
                        className="w-0.75 bg-[#A2A5A9] origin-top"
                    />
                </div>

                <div className="relative w-full">
                    {imageContent}
                    <motion.div
                        initial={{ opacity: 0, scale: 0 }}
                        whileInView={{ opacity: 1, scale: 1 }}
                        viewport={{ once: true, amount: 0.05 }}
                        transition={{ duration: 0.4, delay: 0.5, ease: [0.25, 0.46, 0.45, 0.94] }}
                        className="absolute -top-2 left-1/2 w-3.5 h-3.5 bg-white border-[3.5px] border-[#A2A5A9] rounded-full transform -translate-x-1/2 z-10"
                    />
                </div>
            </div>
        </div>
    );
};

export default ProcessStepV2;
